/**
 * Standalone loadOptions function for the case-user paginated dropdown,
 * designed for use with react-select-async-paginate.
 *
 * listCaseUsersRds pagination:
 *   Request  → `page` param (1-based page number)
 *   Response → `response.data.users` — empty array = no more pages
 *
 * The `additional` object passed through AsyncPaginate carries the current
 * page number between page loads: { page: number }
 */

import { listCaseUsersRds } from 'services/users/userServices';
import { generateFullName } from 'utils/generateFullNameUtils';

const DEFAULT_PAGE_SIZE = 50;

/**
 * Fetches one page of case users from the RDS endpoint.
 *
 * @param {string} search    - Keyword filter
 * @param {number} page      - 1-based page number
 * @param {number} pageSize  - Number of records per page
 * @param {string} caseId    - Case ID to scope the user list
 * @param {boolean} teamMember - Whether to filter to team members only
 * @returns {Promise<{ options, hasMore, additional }>}
 */
const fetchCaseUsersPage = async ({ search = '', page = 1, pageSize = DEFAULT_PAGE_SIZE, caseId = '', teamMember = false }) => {
  const response = await listCaseUsersRds({
    caseId,
    page,
    limit: pageSize,
    keyword: search,
    team_member: teamMember,
    excludeFromLoading: true,
  });

  const users = response?.data?.firm_users || [];

  const options = users.map((user) => ({
    label: generateFullName(user),
    value: user.firm_user_id || user.contact_id || user.client_cognito_username,
    ...user,
  }));

  return {
    options,
    hasMore: users.length === pageSize,
    additional: { page: page + 1 },
  };
};

/**
 * loadOptions function for react-select-async-paginate.
 *
 * Signature: (searchQuery, loadedOptions, additional) => Promise<{ options, hasMore, additional }>
 *
 * @param {string} search         - Current search input
 * @param {Array}  _loadedOptions - Already-loaded options (unused — API handles dedup)
 * @param {Object} additional     - { page: number } current page carried between loads
 * @param {Object} config         - { pageSize?: number, caseId?: string, teamMember?: boolean }
 */
export const loadCaseUserOptions = async (search, _loadedOptions, additional, config = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE, caseId = '', teamMember = false } = config;
  const page = additional?.page || 1;

  try {
    return await fetchCaseUsersPage({ search, page, pageSize, caseId, teamMember });
  } catch (error) {
    console.error('Error loading case user options:', error);
    return { options: [], hasMore: false, additional: { page: 1 } };
  }
};

/**
 * Converts a raw user array (as stored in API responses) into the
 * { label, value, ...user } shape that CaseUserSelect expects as value.
 *
 * @param {Array} users - Array of raw user objects
 * @returns {Array<{ label: string, value: string, ...user }>}
 */
export const toCaseUserOptions = (users = []) =>
  users.map((user) => ({
    label: generateFullName(user),
    value: user.firm_user_id || user.contact_id || user.client_cognito_username || '',
    ...user,
  }));
